import { useBarcodeScannerStatus } from 'app/context/BarcodeScannerContext';

/**
 * Komponen Indikator Status Barcode Scanner di Topbar / Header.
 * Menampilkan status kesiapan scanner hardware (aktif / standby / terdeteksi)
 * serta memberikan feedback visual berupa animasi flash ketika barcode berhasil terbaca.
 *
 * @returns {JSX.Element} Elemen badge status scanner untuk Topbar.
 * @sideEffects Membaca status real-time dari BarcodeScannerContext.
 */
export function TopBarScannerIndicator() {
  const { isListening, activeFeatureInfo, isScanning, lastScanned } = useBarcodeScannerStatus();

  return (
    <div
      className={`inline-flex items-center gap-1.5 sm:gap-2 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-xs font-medium border transition-all duration-300 select-none flex-shrink-0 ${
        isScanning
          ? 'bg-emerald-100 border-emerald-400 text-emerald-800 scale-105 shadow-sm ring-2 ring-emerald-300'
          : isListening
          ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300'
          : 'bg-gray-100 dark:bg-gray-700/40 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-400'
      }`}
      title={
        isScanning
          ? `Barcode terdeteksi: ${lastScanned}`
          : isListening
          ? `Scanner Hardware Aktif (${activeFeatureInfo || 'Siap Memindai'})`
          : 'Scanner Hardware Siap (Menunggu halaman/fitur pemindaian)'
      }
    >
      {/* Ikon Barcode SVG */}
      <svg
        className={`w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 transition-colors ${
          isScanning
            ? 'text-emerald-600'
            : isListening
            ? 'text-blue-600 dark:text-blue-400'
            : 'text-gray-400 dark:text-gray-500'
        }`}
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path d="M2 4h2v16H2V4zm4 0h1v16H6V4zm3 0h2v16H9V4zm4 0h1v16h-1V4zm3 0h2v16h-2V4zm4 0h2v16h-2V4z" />
      </svg>

      {/* Teks Status — selalu tampil di semua ukuran layar */}
      <span className="inline-block text-[11px] sm:text-xs whitespace-nowrap">
        {isScanning
          ? 'Terpindai!'
          : isListening
          ? `Scanner: ${activeFeatureInfo || 'Aktif'}`
          : 'Scanner: Standby'}
      </span>

      {/* Pulsing indicator dot jika listener sedang aktif */}
      {isListening && (
        <span className="relative flex h-2 w-2">
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
              isScanning ? 'bg-emerald-400' : 'bg-blue-400'
            }`}
          />
          <span
            className={`relative inline-flex rounded-full h-2 w-2 ${
              isScanning ? 'bg-emerald-500' : 'bg-blue-500'
            }`}
          />
        </span>
      )}
    </div>
  );
}

export default TopBarScannerIndicator;
